import {
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import classNames from 'classnames';
import { Sidebar as PrimeSidebar } from 'primereact/sidebar';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { BreadCrumb } from 'primereact/breadcrumb';
import { getNavigationTrail } from './navigation.config';
import type { ApiUserRole as ApiUserRoleType } from '@/core/generated/api';
import Sidebar from './Sidebar';
import { UserMenu } from './UserMenu';
import classes from './Layout.module.scss';

const mobileNavigationQuery = '(max-width: 48rem)';

interface LayoutProps {
    logout: () => void;
    roleCode: ApiUserRoleType | null;
    workspaceHeader?: ReactNode;
    pageTitle?: string;

    /** Ограничивает раздел высотой окна с прокруткой внутри его панелей */
    fillViewport?: boolean;
    taskOverdueCount?: number;
}

/** Размещает навигацию и содержимое раздела с выдвижным меню на узком экране */
const Layout = (
    {
        logout,
        roleCode,
        workspaceHeader,
        pageTitle,
        fillViewport = false,
        taskOverdueCount,
    }: LayoutProps
) => {
    const [isMobile, setIsMobile] = useState(() => (
        typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia(mobileNavigationQuery).matches
    ));

    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const [isDesktopCollapsed, setIsDesktopCollapsed] = useState(() => {
        try {
            return localStorage.getItem('vox.sidebar.collapsed.v1') === 'true';
        } catch {
            return false;
        }
    });

    const location = useLocation();
    const navigationTrail = getNavigationTrail(location.pathname);

    useEffect(() => {
        try {
            localStorage.setItem('vox.sidebar.collapsed.v1', String(isDesktopCollapsed));
        } catch {
            return;
        }
    }, [isDesktopCollapsed]);

    const contentRef = useRef<HTMLElement>(null);
    const headerRef = useRef<HTMLElement>(null);

    useLayoutEffect(() => {
        const content = contentRef.current;
        const header = headerRef.current;

        if (!content || !header) return;

        /** Обновляет доступную высоту рабочего пространства */
        const updateHeaderHeight = () => {
            const margin = Number.parseFloat(window.getComputedStyle(header).marginBottom) || 0;
            const height = header.getBoundingClientRect().height + margin;
            content.style.setProperty('--vox-workspace-header-height', `${height}px`);
        };

        updateHeaderHeight();

        const observer = typeof ResizeObserver === 'undefined'
            ? null
            : new ResizeObserver(updateHeaderHeight);

        observer?.observe(header);

        return () => {
            observer?.disconnect();
            content.style.removeProperty('--vox-workspace-header-height');
        };
    }, [workspaceHeader, pageTitle, fillViewport]);

    useEffect(() => {
        if (typeof window.matchMedia !== 'function') return undefined;

        const mediaQuery = window.matchMedia(mobileNavigationQuery);

        /** Переключает навигацию при изменении размера окна */
        const handleViewportChange = (event: MediaQueryListEvent): void => {
            setIsMobile(event.matches);
            setIsMenuOpen(false);
        };

        mediaQuery.addEventListener('change', handleViewportChange);

        return () => mediaQuery.removeEventListener('change', handleViewportChange);
    }, []);

    return (
        <div
            className={classNames(classes.layout, { [classes.viewportLayout]: fillViewport })}
            data-sidebar-collapsed={!isMobile && isDesktopCollapsed}
        >
            {isMobile
                ? (
                    <>
                        <header className={classes.mobileHeader}>
                            <button
                                type="button"
                                className={classes.menuButton}
                                aria-label="Открыть меню"
                                aria-expanded={isMenuOpen}
                                aria-controls="mobile-navigation"
                                onClick={() => setIsMenuOpen(true)}
                            >
                                <span
                                    className="pi pi-bars"
                                    aria-hidden="true"
                                />
                            </button>
                            <span className={classes.mobileTitle}>VOX.Manage</span>
                        </header>

                        <PrimeSidebar
                            id="mobile-navigation"
                            visible={isMenuOpen}
                            onHide={() => setIsMenuOpen(false)}
                            position="left"
                            blockScroll
                            dismissable
                            closeOnEscape
                            showCloseIcon={false}
                            className={classes.mobileSidebar}
                            aria-label="Навигация по админке"
                        >
                            <Sidebar
                                roleCode={roleCode}
                                taskOverdueCount={taskOverdueCount}
                                onClose={() => setIsMenuOpen(false)}
                                onNavigate={() => setIsMenuOpen(false)}
                            />
                        </PrimeSidebar>
                    </>
                )
                : (
                    <div className={`${classes.sidebar} ${isDesktopCollapsed
                        ? classes.sidebarCollapsed
                        : ''}`}>
                        <Sidebar
                            roleCode={roleCode}
                            taskOverdueCount={taskOverdueCount}
                            collapsed={isDesktopCollapsed}
                            onToggleCollapse={() => setIsDesktopCollapsed((value) => !value)}
                        />
                    </div>
                )}

            <main
                ref={contentRef}
                className={classNames(classes.content, classes.contentWithHeader, { [classes.viewportContent]: fillViewport })}
            >
                <header
                    ref={headerRef}
                    className={classes.workspaceHeader}
                >
                    {navigationTrail.length > 0 &&
                        <BreadCrumb
                            aria-label="Путь страницы"
                            className={classes.breadcrumbs}
                            pt={{
                                separator: {
                                    role: 'presentation',
                                    'aria-hidden': true,
                                },
                            }}
                            home={{
                                template: () => <Link
                                    to="/"
                                    aria-label="На главную"
                                >
                                    <i
                                        className="pi pi-home"
                                        aria-hidden="true"
                                    />
                                </Link>
                            }}
                            model={navigationTrail.map((crumb, index) => ({
                                label: crumb.label,
                                template: () => crumb.path && index < navigationTrail.length - 1
                                    ? <Link to={crumb.path}>{crumb.label}</Link>
                                    : <span aria-current={index === navigationTrail.length - 1
                                        ? 'page'
                                        : undefined
                                    }>{crumb.label}</span>,
                            }))}
                        />
                    }
                    {pageTitle &&
                        <h1 className={classes.pageTitle}>{pageTitle}</h1>
                    }
                    {workspaceHeader}
                    <UserMenu
                        login={localStorage.getItem('login') || ''}
                        role={localStorage.getItem('role') || ''}
                        logout={logout}
                    />
                </header>
                <Outlet />
            </main>
        </div>
    );
};

export default Layout;
